import React, { useEffect, useState } from "react";

import { Grid, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AppSwitch } from "Components/Generic/AppSwitch";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Button } from "../../../../../../../components/Form";
import { Color } from "../../../../../../../components/Form/Button";
import BadgeStatus from "../../../../../../../components/Generic/BadgeStatus";
import Skeleton from "../../../../../../../components/Generic/Skeleton";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { TolerancePercentage } from "../../../../../../../models/tolerancePercentage";
import TolerancePercentageService from "../../../../../../../services/tolerancePercentage.service";
import { Status } from "../../../../../../../util/enums";
import useStyles from "./styles";
import { isMoving } from "Util/validation";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const TolerancePorcentage = ({ permission }: Props) => {
  const classes = useStyles();

  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [, setErrors] = useState<yup.ValidationError[]>([]);

  const [hasError, setHasError] = useState<boolean>();

  const [tolerancePercentageToUpdate, setTolerancePercentageToUpdate] =
    useState<TolerancePercentage>();

  const [toleranceActive, setToleranceActive] = useState<TolerancePercentage["isOn"]>(false);

  const tolerancePercentageObject = {
    toleranceActive,
  };

  // const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    // toleranceActive: yup
    // .string()
    // .required(translate("thisFieldIsRequired"))
  });

  const isValid = () => {
    try {
      schema.validateSync(
        {
          ...tolerancePercentageObject,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const { refetch, isFetching } = useQuery(
    ["tolerancePercentage.list"],
    () => TolerancePercentageService.getById(Number(currentUser?.companyId)),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTolerancePercentageToUpdate(response.data);
      },
    }
  );

  const { mutate: updateTolerancePercentage, isLoading: isUpdating } = useMutation(
    ["tolerancePercentage.update"],
    ({ id, tolerancePercentage }: { id: number; tolerancePercentage: TolerancePercentage }) =>
      TolerancePercentageService.update(id, tolerancePercentage),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "request.notification.success.tolerancePercentage",
        });
        setHasError(false);
        refetch();
      },
    }
  );

  const { mutate: createTolerancePercentage, isLoading: isCreating } = useMutation(
    ["tolerancePercentage.create"],
    (tolerancePercentage: TolerancePercentage) =>
      TolerancePercentageService.create(tolerancePercentage),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        setHasError(false);
        refetch();
      },
    }
  );

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (tolerancePercentageToUpdate && tolerancePercentageToUpdate.id) {
      if (tolerancePercentageToUpdate) {
        const object = {
          ...tolerancePercentageToUpdate,
          isOn: toleranceActive,
        };

        if (isValid())
          updateTolerancePercentage({
            id: tolerancePercentageToUpdate.id,
            tolerancePercentage: object as TolerancePercentage,
          });
      }
    } else if (createTolerancePercentage) {
      const object = {
        companyId: currentUser?.companyId,
        isOn: toleranceActive,
      } as TolerancePercentage;

      if (isValid()) createTolerancePercentage(object);
    }
  };

  const handleCancel = () => {
    setToleranceActive(tolerancePercentageToUpdate?.isOn ?? false);
  };

  useEffect(() => {
    if (hasFormBeenSubmitted) isValid();
  }, [
    hasFormBeenSubmitted,
    ...Object.keys(tolerancePercentageObject).map(
      (key) => tolerancePercentageObject[key as keyof typeof tolerancePercentageObject]
    ),
  ]);

  useEffect(() => {
    if (tolerancePercentageToUpdate) {
      setToleranceActive(tolerancePercentageToUpdate?.isOn);
    }
  }, [tolerancePercentageToUpdate]);

  return (
    <div className={classes.tolerancePercentageWrapper}>
      <Grid container spacing={2}>
        <Grid
          className={classes.section}
          container
          item
          xs={12}
          md={12}
          lg={12}
          justifyContent="space-between"
        >
          <Skeleton isLoading={isFetching}>
            <>
              <Grid item>
                <AppSwitch
                  checked={toleranceActive}
                  label={translate("tolerancePercentage")}
                  labelPlacement="end"
                  onChange={(_: any, checked: boolean) => setToleranceActive(checked)}
                />
              </Grid>
              <Grid item>
                <BadgeStatus value={toleranceActive ? Status.ACTIVE : Status.INACTIVE} />
              </Grid>
            </>
          </Skeleton>
          <Grid item xs={12} marginTop="10px">
            <Skeleton isLoading={isFetching}>
              <Typography fontWeight={400} paddingLeft="60px" marginTop="10px" fontFamily="Manrope">
                {translate(
                  "activatingThisWillControlTheTolerancePercentageOfProductsInsideABookingToTheDefinedValue"
                ) + translate("theInputValueWillBeConvertedIntoAPercentage")}
              </Typography>
            </Skeleton>
          </Grid>
        </Grid>

        <Grid
          container
          item
          className={classes.tolerancePercentageFooter}
          justifyContent="flex-end"
        >
          <Grid item>
            <Button
              className={classes.button}
              disabled={isFetching || isUpdating || isCreating}
              customColor={Color.CANCEL}
              onClick={() => handleCancel()}
            >
              {translate("cancel")}
            </Button>
          </Grid>
          {
            (
              (tolerancePercentageToUpdate && tolerancePercentageToUpdate.id && permission && isShowAction(currentUser, permission?.edit)) ||
              (tolerancePercentageToUpdate && !tolerancePercentageToUpdate.id && permission && isShowAction(currentUser, permission?.create)) ||
              !isMoving(currentUser?.company)
            ) && <Grid item>
              <Button
                className={classes.button}
                variant="contained"
                customColor={Color.SUCCESS}
                loading={isUpdating || isCreating}
                disabled={isFetching || hasError}
                onClick={() => handleSubmit()}
              >
                {translate("save")}
              </Button>
            </Grid>
          }
        </Grid>
      </Grid>
    </div>
  );
};

export default TolerancePorcentage;
